import {
  createUserSchema,
  hasPermission,
  resetUserPasswordSchema,
  type CreateUserInput,
  type PublicUser,
  type ResetUserPasswordInput,
} from '@crm/shared'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { KeyRound, UserPlus } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import { Badge, Card, Field, Skeleton } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { api, ApiClientError } from '@/lib/api'
import { fetchMe } from '@/lib/auth-query'
import { formatDateTime } from '@/lib/utils'
import { Route as AppRoute } from '../_app'

export const Route = createFileRoute('/_app/users')({
  /**
   * Client-side gate for UX only — it stops staff *navigating* here. The API
   * enforces `users:manage` independently; this check is not the security
   * boundary and must never be treated as one.
   */
  beforeLoad: async ({ context }) => {
    const me = await fetchMe(context.queryClient)
    if (me && !hasPermission(me.user.role, 'users:manage')) {
      throw redirect({ to: '/' })
    }
  },
  component: UsersPage,
})

const USERS_KEY = ['users'] as const

function UsersPage() {
  const { me } = AppRoute.useRouteContext()
  const [showCreate, setShowCreate] = useState(false)
  const [resetting, setResetting] = useState<PublicUser | null>(null)

  const { data, isLoading, isError } = useQuery({
    queryKey: USERS_KEY,
    queryFn: () => api.get<{ items: PublicUser[] }>('/api/users?includeInactive=true'),
  })

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Staff</h1>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Manage who can sign in and what they can do.
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)}>
          <UserPlus className="h-4 w-4" />
          Add user
        </Button>
      </div>

      <Card className="overflow-hidden">
        {isLoading && (
          <div className="space-y-2 p-6">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-3/4" />
          </div>
        )}
        {isError && (
          <p className="p-6 text-sm text-[var(--color-destructive)]">Couldn&rsquo;t load users.</p>
        )}

        {data && data.items.length === 0 && (
          <div className="flex flex-col items-center gap-2 p-6 text-center">
            <UserPlus className="h-6 w-6 text-[var(--color-muted-foreground)]" />
            <p className="text-sm text-[var(--color-muted-foreground)]">No users yet.</p>
          </div>
        )}

        {data && data.items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-[var(--color-border)] bg-[var(--color-muted)]/50 text-left">
                <tr>
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="px-4 py-2 font-medium">Email</th>
                  <th className="px-4 py-2 font-medium">Role</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Last sign-in</th>
                  <th className="px-4 py-2 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((user) => (
                  <UserRow
                    key={user.id}
                    user={user}
                    isSelf={user.id === me.user.id}
                    onReset={() => setResetting(user)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {showCreate && <CreateUserDialog onClose={() => setShowCreate(false)} />}
      {resetting && <ResetPasswordDialog user={resetting} onClose={() => setResetting(null)} />}
    </div>
  )
}

function UserRow({
  user,
  isSelf,
  onReset,
}: {
  user: PublicUser
  isSelf: boolean
  onReset: () => void
}) {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const toggleActive = useMutation({
    mutationFn: (active: boolean) => api.patch(`/api/users/${user.id}`, { active }),
    onSuccess: () => {
      setError(null)
      void queryClient.invalidateQueries({ queryKey: USERS_KEY })
    },
    // The server owns rules like "last active admin" — surface its reason verbatim
    // rather than trying to duplicate the rule here and drift from it.
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Update failed'),
  })

  return (
    <tr className="border-b border-[var(--color-border)] last:border-0">
      <td className="px-4 py-3">
        {user.name}
        {isSelf && <span className="ml-2 text-xs text-[var(--color-muted-foreground)]">(you)</span>}
        {error && <p className="mt-1 text-xs text-[var(--color-destructive)]">{error}</p>}
      </td>
      <td className="px-4 py-3 text-[var(--color-muted-foreground)]">{user.email}</td>
      <td className="px-4 py-3">
        <Badge variant={user.role === 'admin' ? 'default' : 'muted'}>{user.role}</Badge>
      </td>
      <td className="px-4 py-3">
        {user.active ? (
          <Badge variant="muted">Active</Badge>
        ) : (
          <Badge variant="destructive">Inactive</Badge>
        )}
      </td>
      <td className="px-4 py-3 text-[var(--color-muted-foreground)]">
        {formatDateTime(user.lastLoginAt)}
      </td>
      <td className="px-4 py-3">
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onReset}>
            <KeyRound className="h-3.5 w-3.5" />
            Reset password
          </Button>
          <Button
            variant={user.active ? 'outline' : 'default'}
            size="sm"
            disabled={isSelf}
            title={isSelf ? 'You cannot deactivate your own account' : undefined}
            loading={toggleActive.isPending}
            onClick={() => toggleActive.mutate(!user.active)}
          >
            {user.active ? 'Deactivate' : 'Reactivate'}
          </Button>
        </div>
      </td>
    </tr>
  )
}

function Dialog({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <Card className="w-full max-w-sm p-6">
        <h2 className="mb-4 text-lg font-semibold">{title}</h2>
        {children}
      </Card>
    </div>
  )
}

function CreateUserDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient()
  const [formError, setFormError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<CreateUserInput>({
    resolver: zodResolver(createUserSchema),
    defaultValues: { email: '', name: '', role: 'staff', password: '' },
  })

  const mutation = useMutation({
    mutationFn: (input: CreateUserInput) => api.post('/api/users', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: USERS_KEY })
      onClose()
    },
    onError: (e) => {
      if (e instanceof ApiClientError && e.code === 'conflict') {
        setError('email', { message: e.message })
        return
      }
      setFormError(e instanceof ApiClientError ? e.message : 'Could not create the user')
    },
  })

  return (
    <Dialog title="Add user">
      <form className="space-y-4" onSubmit={handleSubmit((v) => mutation.mutate(v))} noValidate>
        {formError && (
          <div role="alert" className="text-sm text-[var(--color-destructive)]">
            {formError}
          </div>
        )}

        <Field label="Name" htmlFor="name" error={errors.name?.message} required>
          <Input id="name" autoFocus aria-invalid={!!errors.name} {...register('name')} />
        </Field>

        <Field label="Email" htmlFor="new-email" error={errors.email?.message} required>
          <Input id="new-email" type="email" aria-invalid={!!errors.email} {...register('email')} />
        </Field>

        <Field label="Role" htmlFor="role" error={errors.role?.message} required>
          <select
            id="role"
            className="h-10 w-full rounded-md border border-[var(--color-input)] bg-[var(--color-background)] px-3 text-sm"
            {...register('role')}
          >
            <option value="staff">Staff</option>
            <option value="admin">Admin</option>
          </select>
        </Field>

        <Field
          label="Temporary password"
          htmlFor="new-password"
          error={errors.password?.message}
          hint="At least 10 characters. Share it securely; they can change it after signing in."
          required
        >
          <Input
            id="new-password"
            type="text"
            autoComplete="off"
            aria-invalid={!!errors.password}
            {...register('password')}
          />
        </Field>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={mutation.isPending}>
            Create
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

function ResetPasswordDialog({ user, onClose }: { user: PublicUser; onClose: () => void }) {
  const [formError, setFormError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetUserPasswordInput>({
    resolver: zodResolver(resetUserPasswordSchema),
    defaultValues: { newPassword: '' },
  })

  const mutation = useMutation({
    mutationFn: (input: ResetUserPasswordInput) =>
      api.post(`/api/users/${user.id}/reset-password`, input),
    onSuccess: onClose,
    onError: (e) => setFormError(e instanceof ApiClientError ? e.message : 'Could not reset'),
  })

  return (
    <Dialog title={`Reset password — ${user.name}`}>
      <form className="space-y-4" onSubmit={handleSubmit((v) => mutation.mutate(v))} noValidate>
        {formError && (
          <div role="alert" className="text-sm text-[var(--color-destructive)]">
            {formError}
          </div>
        )}

        <p className="text-sm text-[var(--color-muted-foreground)]">
          This signs {user.name} out of every device immediately.
        </p>

        <Field
          label="New password"
          htmlFor="reset-password"
          error={errors.newPassword?.message}
          hint="At least 10 characters."
          required
        >
          <Input
            id="reset-password"
            type="text"
            autoComplete="off"
            autoFocus
            aria-invalid={!!errors.newPassword}
            {...register('newPassword')}
          />
        </Field>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={mutation.isPending}>
            Reset password
          </Button>
        </div>
      </form>
    </Dialog>
  )
}
